<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useThemeStore } from '@/stores/theme'

import scanwayLight from '@/assets/brand/scanway-light.png'
import scanwayDark from '@/assets/brand/scanway-dark.png'
import hydraLight from '@/assets/brand/hydra-light.svg'
import hydraDark from '@/assets/brand/hydra-dark.svg'

/**
 * Theme-aware brand logo. Swaps between the light- and dark-background
 * artwork off the theme store's *effective* theme (the same `.dark` signal
 * the rest of the UI reacts to). One component serves both marks:
 *
 *  - `scanway` — the company name. Primary brand mark.
 *  - `hydra`   — the data-processing product family. Secondary, smaller.
 *
 * The "light" variant carries dark ink for light backgrounds; the "dark"
 * variant is the light-ink artwork for dark backgrounds. Naming follows the
 * theme it renders in, not the ink colour.
 *
 * The platform's internal codename ("eoscope") is intentionally never shown
 * to users — these logos are the only user-facing brand identity.
 */

const props = defineProps<{
  name: 'scanway' | 'hydra'
  /** Accessible label. Defaults to a capitalised brand name. */
  alt?: string
}>()

const { effectiveTheme } = storeToRefs(useThemeStore())

const SOURCES = {
  scanway: { light: scanwayLight, dark: scanwayDark },
  hydra: { light: hydraLight, dark: hydraDark },
} as const

const src = computed(() => SOURCES[props.name][effectiveTheme.value])
const label = computed(
  () => props.alt ?? (props.name === 'scanway' ? 'Scanway' : 'Hydra'),
)
</script>

<template>
  <img class="brand-logo" :src="src" :alt="label" />
</template>

<style scoped>
.brand-logo {
  display: block;
  /* Height is owned by the consumer via a wrapping class or inline style;
     width tracks the aspect ratio. A sane default keeps it usable bare. */
  height: 24px;
  width: auto;
  user-select: none;
}
</style>
